import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  apiKeyService,
  APIKey,
  APIKeyResponse,
} from '../services/apiKeyService';
import axios from 'axios';
import { getAuthHeaders } from '../utils/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_URL } from '../constants/urls';

interface UserUpdateData {
  first_name: string;
  last_name: string;
  email: string;
  role_title: string;
  role_description: string;
}

const AccountManagement: React.FC = () => {
  const { user, refreshUserData } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<
    'personal' | 'api-keys' | 'security'
  >('personal');
  const [formData, setFormData] = useState<UserUpdateData>({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || '',
    role_title: user?.role_title || '',
    role_description: user?.role_description || '',
  });
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);

  // API Key management state
  const [apiKeys, setApiKeys] = useState<APIKey[]>([]);
  const [isLoadingKeys, setIsLoadingKeys] = useState(false);
  const [showCreateKeyModal, setShowCreateKeyModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [createdKey, setCreatedKey] = useState<APIKeyResponse | null>(null);
  const [keyToDelete, setKeyToDelete] = useState<string | null>(null);
  const [isQuickGenerating, setIsQuickGenerating] = useState(false);

  // Handle navigation state to auto-select tab
  useEffect(() => {
    const state = location.state as { activeTab?: string } | null;
    if (state?.activeTab === 'api-keys') {
      setActiveTab('api-keys');
    }
  }, [location]);

  useEffect(() => {
    loadAPIKeys();
  }, []);

  const loadAPIKeys = async () => {
    setIsLoadingKeys(true);
    try {
      const keys = await apiKeyService.listAPIKeys();
      setApiKeys(keys);
    } catch (error) {
      console.error('Failed to load API keys:', error);
    } finally {
      setIsLoadingKeys(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await axios.put(`${API_URL}/auth/me`, formData, {
        headers: getAuthHeaders(),
      });
      if (response.status === 200) {
        setSuccess('Account details updated successfully');
        setIsEditing(false);
        await refreshUserData();
      }
    } catch (err: any) {
      setError(
        err.response?.data?.detail || 'Failed to update account details',
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      email: user?.email || '',
      role_title: user?.role_title || '',
      role_description: user?.role_description || '',
    });
    setIsEditing(false);
    setError(null);
  };

  const handleCreateAPIKey = async () => {
    if (!newKeyName.trim()) {
      setError('Please enter a name for the API key');
      return;
    }

    try {
      const response = await apiKeyService.createAPIKey({ name: newKeyName });
      setCreatedKey(response);
      setNewKeyName('');
      await loadAPIKeys();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create API key');
    }
  };

  const handleRevokeAPIKey = async (keyId: string) => {
    try {
      await apiKeyService.revokeAPIKey(keyId);
      setSuccess('API key revoked successfully');
      await loadAPIKeys();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to revoke API key');
    }
  };

  const handleDeleteAPIKey = async (keyId: string) => {
    try {
      await apiKeyService.deleteAPIKey(keyId);
      setSuccess('API key deleted permanently');
      await loadAPIKeys();
      setKeyToDelete(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete API key');
    }
  };

  const handleQuickGenerateAPIKey = async () => {
    if (isQuickGenerating) {
      return;
    }

    setIsQuickGenerating(true);
    try {
      const timestamp = new Date().toISOString().split('T')[0];
      const response = await apiKeyService.createAPIKey({
        name: `Quick Key - ${timestamp}`,
      });
      setCreatedKey(response);
      await loadAPIKeys();
      setSuccess('API key generated successfully');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create API key');
    } finally {
      setIsQuickGenerating(false);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setSuccess('Copied to clipboard!');
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      setError('Failed to copy to clipboard');
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Account Management
        </h1>
        <p className="text-gray-600">
          Manage your account details and security settings
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('personal')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'personal'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Personal Information
          </button>
          <button
            onClick={() => setActiveTab('api-keys')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'api-keys'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            API Keys
          </button>
          <button
            onClick={() => setActiveTab('security')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'security'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Security
          </button>
        </nav>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
          <span className="block sm:inline">{error}</span>
          <button
            className="absolute top-0 bottom-0 right-0 px-4 py-3"
            onClick={() => setError(null)}
          >
            <span className="text-red-500">×</span>
          </button>
        </div>
      )}

      {success && (
        <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded relative">
          <span className="block sm:inline">{success}</span>
          <button
            className="absolute top-0 bottom-0 right-0 px-4 py-3"
            onClick={() => setSuccess(null)}
          >
            <span className="text-green-500">×</span>
          </button>
        </div>
      )}

      {/* Tab Content */}
      <div className="space-y-6">
        {/* Personal Information Tab */}
        {activeTab === 'personal' && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                Personal Information
              </h2>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name
                  </label>
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    className={`w-full px-3 py-2 border rounded-md ${
                      isEditing
                        ? 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                        : 'border-gray-200 bg-gray-50'
                    }`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name
                  </label>
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    className={`w-full px-3 py-2 border rounded-md ${
                      isEditing
                        ? 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                        : 'border-gray-200 bg-gray-50'
                    }`}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    className={`w-full px-3 py-2 border rounded-md ${
                      isEditing
                        ? 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                        : 'border-gray-200 bg-gray-50'
                    }`}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Role Title
                  </label>
                  <input
                    type="text"
                    name="role_title"
                    value={formData.role_title}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    placeholder="e.g., Product Manager, Engineering Lead"
                    className={`w-full px-3 py-2 border rounded-md ${
                      isEditing
                        ? 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                        : 'border-gray-200 bg-gray-50'
                    }`}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Role Description
                  </label>
                  <textarea
                    name="role_description"
                    value={formData.role_description}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    rows={3}
                    placeholder="Brief description of your role and responsibilities..."
                    className={`w-full px-3 py-2 border rounded-md ${
                      isEditing
                        ? 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                        : 'border-gray-200 bg-gray-50'
                    }`}
                  />
                </div>
              </div>

              <div className="mt-6 flex gap-3">
                {!isEditing ? (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Edit Information
                  </button>
                ) : (
                  <>
                    <button
                      onClick={handleSave}
                      disabled={isSaving}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                      {isSaving ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button
                      onClick={handleCancel}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* API Keys Tab */}
        {activeTab === 'api-keys' && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-900">
                  API Keys
                </h2>
                <div className="flex gap-2">
                  <button
                    onClick={handleQuickGenerateAPIKey}
                    disabled={isQuickGenerating}
                    className="px-4 py-2 bg-purple-600 text-white text-sm rounded-md hover:bg-purple-700 flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                    title="Generate a key instantly with automatic naming"
                  >
                    {isQuickGenerating ? (
                      <>
                        <svg
                          className="w-4 h-4 animate-spin"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        Generating...
                      </>
                    ) : (
                      <>
                        <span>⚡</span>
                        <span>Quick Generate</span>
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => setShowCreateKeyModal(true)}
                    className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
                    title="Generate a key with custom name"
                  >
                    Custom Key
                  </button>
                </div>
              </div>
            </div>
            <div className="p-6">
              {isLoadingKeys ? (
                <div className="text-center py-4">Loading API keys...</div>
              ) : apiKeys.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>No API keys yet.</p>
                  <p className="mt-2 text-sm">
                    Create an API key to use with the MCP server or API
                    integrations.
                  </p>
                </div>
              ) : (
                <>
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <p className="text-sm text-blue-800">
                      <strong>Note:</strong> For security, full API keys are
                      only shown once when created. The prefix shown here can
                      help you identify which key is which. Store your API keys
                      securely - they cannot be retrieved after creation.
                    </p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead>
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Name
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Key Prefix
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Created
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Last Used
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {apiKeys.map((key) => (
                          <tr
                            key={key.id}
                            className={!key.is_active ? 'bg-gray-50' : ''}
                          >
                            <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {key.name}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                              <span className="font-mono">{key.prefix}...</span>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                              {new Date(key.created_at).toLocaleDateString()}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                              {key.last_used_at
                                ? new Date(
                                    key.last_used_at,
                                  ).toLocaleDateString()
                                : 'Never'}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm">
                              {key.is_active ? (
                                <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded">
                                  Active
                                </span>
                              ) : (
                                <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">
                                  Revoked
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm">
                              <div className="flex items-center gap-3">
                                {key.is_active ? (
                                  <button
                                    onClick={() => handleRevokeAPIKey(key.id)}
                                    className="text-yellow-600 hover:text-yellow-700"
                                    title="Revoke this key (disable but keep for records)"
                                  >
                                    Revoke
                                  </button>
                                ) : (
                                  <span className="text-gray-400 italic">
                                    Revoked
                                  </span>
                                )}
                                <button
                                  onClick={() => setKeyToDelete(key.id)}
                                  className="text-red-600 hover:text-red-900"
                                  title="Permanently delete this key"
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {/* Help Text */}
                  <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-md">
                    <p className="text-sm text-purple-800">
                      <strong>💡 Tip:</strong> Use API keys to connect Claude
                      Desktop to Spotlight.{' '}
                      <button
                        onClick={() => navigate('/claude-instructions')}
                        className="underline hover:text-purple-900"
                      >
                        View setup instructions →
                      </button>
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Security Tab */}
        {activeTab === 'security' && (
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Security Settings
            </h2>
            <div className="text-gray-600">
              <p className="mb-4">
                Security features are coming soon. This will include:
              </p>
              <ul className="list-disc list-inside space-y-2 text-sm">
                <li>Password change</li>
                <li>Two-factor authentication</li>
                <li>Session management</li>
                <li>Login history</li>
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* Modals - Outside tab content but inside main container */}
      {/* Create API Key Modal */}
      {showCreateKeyModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div
              className="fixed inset-0 bg-gray-500/75"
              onClick={() => {
                setShowCreateKeyModal(false);
                setNewKeyName('');
              }}
            />
            <div className="relative bg-white rounded-lg max-w-md w-full p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Generate New API Key
              </h3>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Key Name
                </label>
                <input
                  type="text"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  placeholder="e.g., MCP Server Key"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="mt-1 text-sm text-gray-500">
                  A descriptive name to identify this key
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleCreateAPIKey}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Generate Key
                </button>
                <button
                  onClick={() => {
                    setShowCreateKeyModal(false);
                    setNewKeyName('');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Show Created Key Modal */}
      {createdKey && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-500/75" />
            <div className="relative bg-white rounded-lg max-w-2xl w-full p-6">
              <div className="mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  API Key Created Successfully
                </h3>
                <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded">
                  <p className="text-sm text-yellow-800">
                    ⚠️ This is the only time you&apos;ll see this key. Copy it
                    now and store it securely.
                  </p>
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Your API Key
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={createdKey.key}
                    readOnly
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 font-mono text-sm"
                  />
                  <button
                    onClick={() => copyToClipboard(createdKey.key)}
                    className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                  >
                    Copy
                  </button>
                </div>
              </div>
              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  <strong>Name:</strong> {createdKey.name}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Prefix:</strong> {createdKey.prefix}...
                </p>
              </div>
              <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded">
                <p className="text-sm text-purple-800">
                  Ready to use this key with Claude Desktop?{' '}
                  <button
                    onClick={() => {
                      navigate('/claude-instructions');
                      // Pass the key via session storage for auto-fill
                      sessionStorage.setItem('latestApiKey', createdKey.key);
                    }}
                    className="underline hover:text-purple-900 font-medium"
                  >
                    Go to setup instructions →
                  </button>
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setCreatedKey(null);
                    setShowCreateKeyModal(false);
                  }}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {keyToDelete && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div
              className="fixed inset-0 bg-gray-500/75"
              onClick={() => setKeyToDelete(null)}
            />
            <div className="relative bg-white rounded-lg max-w-md w-full p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Delete API Key
              </h3>
              <p className="text-gray-600 mb-6">
                Are you sure you want to permanently delete this API key? This
                will remove it completely from the system. This action cannot be
                undone.
              </p>
              <p className="text-sm text-gray-500 mb-6">
                <strong>Tip:</strong> If you just want to disable the key but
                keep it for records, use &ldquo;Revoke&rdquo; instead.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => handleDeleteAPIKey(keyToDelete)}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                >
                  Delete Permanently
                </button>
                <button
                  onClick={() => setKeyToDelete(null)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountManagement;
